import React, { useState } from 'react';
import { ArrowRight, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useRecaptcha } from '../hooks/useRecaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getErrorMessage = (error) => {
  if (!error.response?.data?.detail) {
    return 'Failed to submit quote request. Please try again or call us directly.';
  }
  
  const detail = error.response.data.detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg).join(', ');
  }
  return detail;
};

export const QuoteForm = () => {
  const { executeRecaptcha } = useRecaptcha();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
    requestCallback: false
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData for multipart/form-data submission
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('service', formData.service);
      submitData.append('message', formData.message);
      submitData.append('requestCallback', formData.requestCallback);
      if (executeRecaptcha) {
        try {
          submitData.append('recaptchaToken', await executeRecaptcha('quote_submission'));
        } catch (err) {
          console.warn('[recaptcha] token unavailable, submitting without it', err);
        }
      }

      // Append files
      files.forEach((file) => {
        submitData.append('files', file);
      });

      // Submit to backend
      const response = await axios.post(
        `${BACKEND_URL}/api/quotes`,
        submitData
        // Don't set Content-Type manually - axios sets it automatically with boundary
      );

      toast.success('Quote request submitted successfully! We\'ll contact you shortly.');

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: '',
        requestCallback: false
      });
      setFiles([]);

      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 bg-neutral-50" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="font-display uppercase tracking-[0.25em] text-sm font-semibold text-red-600">Free Estimate</span>
          <h2 className="font-display italic uppercase text-4xl md:text-5xl font-extrabold text-neutral-950 mt-2 mb-3">Get a Custom Quote</h2>
          <p className="text-neutral-600 text-base md:text-lg">
            Send us photos of your vehicle for accurate pricing
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 border-t-4 border-t-red-600 shadow-xl shadow-neutral-900/5 rounded-sm p-8 md:p-12" data-testid="quote-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-neutral-900 font-semibold mb-2">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
              data-testid="quote-input-name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-red-600 transition-colors duration-200"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-neutral-900 font-semibold mb-2">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
              data-testid="quote-input-phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-red-600 transition-colors duration-200"
                placeholder="(505) 555-0123"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="email" className="block text-neutral-900 font-semibold mb-2">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              data-testid="quote-input-email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-red-600 transition-colors duration-200"
              placeholder="your@email.com"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="service" className="block text-neutral-900 font-semibold mb-2">Service *</label>
            <select
              id="service"
              name="service"
              data-testid="quote-input-service"
              value={formData.service}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-sm text-neutral-900 focus:outline-none focus:border-red-600 transition-colors duration-200"
            >
              <option value="">Select a service</option>
              <option value="interior">Interior Detail</option>
              <option value="exterior">Exterior Detail</option>
              <option value="full">Full Detail</option>
              <option value="paint-enhancement">Paint Enhancement</option>
              <option value="paint-correction">Paint Correction</option>
              <option value="ceramic-coating">Ceramic Coating</option>
              <option value="rv">RV Wash & Wax</option>
              <option value="headlight">Headlight Restoration</option>
              <option value="engine">Engine Bay Cleaning</option>
            </select>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-neutral-900 font-semibold mb-2">Message</label>
            <textarea
              id="message"
              name="message"
              data-testid="quote-input-message"
              value={formData.message}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-red-600 transition-colors duration-200 resize-none"
              placeholder="Tell us about your vehicle and any specific concerns..."
            ></textarea>
          </div>

          <div className="mb-6">
            <label className="block text-neutral-900 font-semibold mb-2">Vehicle Photos</label>
            <div className="relative">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center w-full px-4 py-8 bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-sm cursor-pointer hover:border-red-600 transition-colors duration-200"
              >
                <div className="text-center">
                  <Upload className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <span className="text-neutral-700 font-semibold">Click to upload photos</span>
                  <p className="text-neutral-500 text-sm mt-1">PNG, JPG up to 10MB each</p>
                </div>
              </label>
            </div>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between bg-neutral-100 px-4 py-2 rounded-sm">
                    <span className="text-neutral-800 text-sm truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-8">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="requestCallback"
              data-testid="quote-input-requestCallback"
                checked={formData.requestCallback}
                onChange={handleInputChange}
                className="w-5 h-5 border border-neutral-300 rounded accent-red-600 text-red-600 focus:ring-red-600"
              />
              <span className="text-neutral-800">Request a Call Back</span>
            </label>
          </div>

          <button
            type="submit"
            data-testid="quote-submit-btn"
            disabled={isSubmitting}
            className="w-full group px-8 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white font-display uppercase tracking-wider text-lg font-bold rounded-sm transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-red-600/50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Submit Request</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};
